import Icon1 from "@assets/icons/Icon";
import { Text1 } from "@components/ui/texts/Texts";
import { useState, useEffect } from "react";
import { Broadcast } from "types";
import { navigateTo } from "context/navigation.service";

import { useDataStore } from "@store/user_org";
import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import { useParams } from "react-router-dom";
import { Project } from "types";
import { StatusBlock } from "@components/ui/blocks/Status";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import { VerticalTimeline } from "@components/ui/blocks/VericalTimeline";
import { StepsList } from "@components/ui/blocks/StepsList";
import ProjectScreenSkeleton from "./ProjectScreenSkeleton";
import SearchField from "@components/ui/textfields/SearchField";
import { PhotoModal } from "@components/modals/PhotoModal";
import { DocumentSection } from "@components/ui/blocks/DocumentSection";
import { PartnersSection } from "@components/ui/blocks/PartnersSection";
import { useProjectStore } from "@store/projectStore";
import { isGUEST } from "@helpers/accessHelpers";
import { ActionsMenuButtonProject } from "@components/ui/buttons/ActionsMenuButtonProject";
import { CustomImage } from "@components/ui/images/CustomImage";
import { InfosSection } from "@components/ui/blocks/InfosSection";
import { PlanningTable } from "@components/ui/blocks/PlanningTable";
import { PlanningSetupModal } from "@components/modals/PlanningSetupModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { Planning } from "types/planning";
import { planningsApi } from "@services/api/plannings";
import { showNotification } from "context/notification.service";

export default function ProjectScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const isGuest = isGUEST(userData);
  const {
    sentEmails,
    setSentEmails,
    fetchProject,
    fetchSentEmails,
    updateProject,
    plannings,
    fetchPlannings,
    addPlanning,
    updatePlanning,
    removePlannings,
    broadcasts,
  } = useProjectStore();
  const [expandedBroadcast, setExpandedBroadcast] = useState<string | null>(
    null,
  );
  const params = useParams();
  const id = params.id;
  const [isLoading, setIsLoading] = useState(true);
  const [project, setProject] = useState<Project | undefined>(undefined);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetchProject(id).then((p) => {
        setProject(p);
        setIsLoading(false);
      });
      if (orgData?.id) {
        fetchSentEmails(orgData.id, id);
        fetchPlannings(orgData.id, id);
      }
    }
  }, [id]);
  const [createFolderOpen, setCreateFolderOpen] = useState<boolean>(false);
  const [timelineSearch, setTimelineSearch] = useState<string>("");
  const [planningSetupOpen, setPlanningSetupOpen] = useState(false);
  const [planningEditOpen, setPlanningEditOpen] = useState(false);

  const projectPlannings = plannings.filter((p) => p.project_id === id);
  const activePlanning = projectPlannings[0] as Planning | undefined;

  const [activeTab, setActiveTab] = useState<string>("timeline");
  const [activePhotoTab, setActivePhotoTab] = useState<string>("date");
  const [activeDocumentTab, setActiveDocumentTab] = useState<string>("folder");

  const handleSetProject = (updated: Project) => {
    setProject(updated);
    updateProject(updated);
  };

  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  if (isLoading || !project) {
    return <ProjectScreenSkeleton />;
  }
  return (
    <div className="flex flex-col bg-neutral-100 overflow-auto max-h-screen">
      {createFolderOpen && (
        <CreateProjectModal
          onClose={() => setCreateFolderOpen(false)}
        ></CreateProjectModal>
      )}
      {selectedPhoto && (
        <PhotoModal
          onClose={() => setSelectedPhoto(null)}
          photo_id={selectedPhoto}
          activeProject={project}
          setActiveProject={handleSetProject}
        />
      )}
      <div className="flex flex-col h-fit pb-96 mt-10 ml-10 mr-10">
        <div className="flex flex-col w-full max-w-1p2k">
          <div className="flex flex-row w-full justify-start mb-10">
            <div className="flex flex-col w-fit mr-4 gap-2 ">
              <div
                className="text-h3 text-neutral-400 hover:text-neutral-600 cursor-pointer"
                onClick={() => navigateTo({ route: "/projects" })}
              >
                Projets
              </div>
              <Text1>{project?.infos.general.name || "Projet sans nom"}</Text1>
              <StatusBlock type="info" label="Terrassement"></StatusBlock>{" "}
              <StatusBlock type="success" label="Accès chantier"></StatusBlock>
            </div>
            {project?.infos.general.photo_file?.url ? (
              <img
                src={project.infos.general.photo_file.url}
                className="h-28 w-56 rounded-3xl object-cover mr-auto"
              />
            ) : (
              <div className="h-28 w-56 rounded-3xl bg-white mr-auto flex items-center justify-center shadow-inner border border-neutral-200">
                <Icon1 name="image" size={24} color="grey" />
              </div>
            )}

            {!isGuest && (
              <ActionsMenuButtonProject
                project={project}
                setProject={setProject}
              />
            )}
          </div>

          {/* Active broadcasts banner (guest view) */}
          {isGuest &&
            (() => {
              const now = new Date();
              const activeBroadcasts = broadcasts.filter((b: Broadcast) => {
                if (b.status !== "active") return false;
                const start = b.start_date?.toDate
                  ? b.start_date.toDate()
                  : new Date((b.start_date as any).seconds * 1000);
                const end = b.end_date?.toDate
                  ? b.end_date.toDate()
                  : new Date((b.end_date as any).seconds * 1000);
                return now >= start && now <= end;
              });
              if (activeBroadcasts.length === 0) return null;
              return (
                <div className="flex flex-col gap-2 mb-4 w-full max-w-1p2k">
                  {activeBroadcasts.map((b: Broadcast) => (
                    <div
                      key={b.id}
                      className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 cursor-pointer"
                      onClick={() =>
                        setExpandedBroadcast(
                          expandedBroadcast === b.id ? null : b.id,
                        )
                      }
                    >
                      <div className="flex flex-row items-center gap-2">
                        <Icon1 name="broadcast" size={16} color="blue" />
                        <div className="flex-1 text-h2 font-semibold text-blue-800">
                          {b.title}
                        </div>
                        <Icon1
                          name={expandedBroadcast === b.id ? "up" : "down"}
                          size={12}
                          color="blue"
                        />
                      </div>
                      {expandedBroadcast === b.id && (
                        <div className="mt-2 flex flex-col gap-1">
                          <div className="text-h3 text-blue-700 whitespace-pre-wrap">
                            {b.content}
                          </div>
                          <div className="text-h4 text-blue-400 mt-1">
                            Du{" "}
                            {(b.start_date?.toDate
                              ? b.start_date.toDate()
                              : new Date((b.start_date as any).seconds * 1000)
                            ).toLocaleDateString("fr-FR")}{" "}
                            au{" "}
                            {(b.end_date?.toDate
                              ? b.end_date.toDate()
                              : new Date((b.end_date as any).seconds * 1000)
                            ).toLocaleDateString("fr-FR")}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}

          <div className="flex flex-row gap-8 items-start">
            <div className="flex flex-col flex-1 min-w-0">
              <ChunkyTabs
                tabs={projectTabs}
                activeTab={activeTab}
                setActiveTab={(value) => setActiveTab(value)}
              ></ChunkyTabs>

              <div className="flex flex-col h-fit mt-4 gap-4">
                {activeTab === "timeline" && (
                  <div className="flex flex-col w-500 gap-8">
                    <SearchField
                      value={timelineSearch}
                      onChange={(value) => setTimelineSearch(value)}
                      placeholder="Terrassement .."
                    ></SearchField>
                    <VerticalTimeline
                      history_events={project.history_events}
                      activeProject={project}
                      setActiveProject={handleSetProject}
                      sentEmails={sentEmails}
                      setSentEmails={setSentEmails}
                      userData={userData || undefined}
                      orgData={orgData || undefined}
                      search={timelineSearch}
                      isGuest={isGuest}
                    ></VerticalTimeline>
                  </div>
                )}
                {activeTab === "steps" && (
                  <StepsList
                    label=""
                    setProject={setProject}
                    project={project!}
                    userData={userData || undefined}
                    readOnly={isGuest}
                    isGuest={isGuest}
                  />
                )}
                {activeTab === "photos" && (
                  <div className="flex flex-col w-500 gap-8">
                    <ChunkyTabs
                      tabs={photoTabs}
                      activeTab={activePhotoTab}
                      setActiveTab={(value) => setActivePhotoTab(value)}
                    ></ChunkyTabs>
                    <div className="">
                      {activePhotoTab === "date" && (
                        <VerticalTimeline
                          history_events={project.history_events.filter(
                            (e) => e.photos_id && e.photos_id.length > 0,
                          )}
                          activeProject={project}
                          setActiveProject={handleSetProject}
                          sentEmails={sentEmails}
                          orgData={orgData || undefined}
                          userData={userData || undefined}
                          isGuest={isGuest}
                        ></VerticalTimeline>
                      )}
                      {activePhotoTab === "step" && (
                        <StepsList
                          label=""
                          setProject={setProject}
                          project={project!}
                          userData={userData || undefined}
                          show_photos={true}
                          isGuest={isGuest}
                        ></StepsList>
                      )}
                      {activePhotoTab === "gallery" && (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] max-w-full gap-4">
                          {project.photos
                            .filter((photo) => (isGuest ? !photo.hidden : true))
                            .map((photo, index) => (
                              <CustomImage
                                key={index}
                                source={{ uri: photo.file.url }}
                                className="w-full aspect-square rounded-xl cursor-pointer"
                                hidden={photo.hidden}
                                alt={`Photo ${index + 1}`}
                                onClick={() => setSelectedPhoto(photo.id)}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "documents" && (
                  <div className="flex flex-col w-500 gap-8">
                    <ChunkyTabs
                      tabs={documentTabs}
                      activeTab={activeDocumentTab}
                      setActiveTab={(value) => setActiveDocumentTab(value)}
                    ></ChunkyTabs>
                    <div className="">
                      {activeDocumentTab === "folder" && (
                        <DocumentSection
                          folders={project.folders}
                          activeProject={project}
                          setActiveProject={handleSetProject}
                          readOnly={isGuest}
                        />
                      )}
                      {activeDocumentTab === "date" && (
                        <VerticalTimeline
                          history_events={project.history_events.filter(
                            (e) => e.documents_id && e.documents_id.length > 0,
                          )}
                          activeProject={project}
                          setActiveProject={handleSetProject}
                          sentEmails={sentEmails}
                          orgData={orgData || undefined}
                          isGuest={isGuest}
                        ></VerticalTimeline>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "partners" && (
                  <PartnersSection
                    project={project}
                    setProject={handleSetProject}
                    readOnly={isGuest}
                  />
                )}
                {activeTab === "planning" && (
                  <div className="flex flex-col gap-4">
                    {planningSetupOpen && (
                      <PlanningSetupModal
                        onClose={() => setPlanningSetupOpen(false)}
                        onCreated={(p) => addPlanning(p)}
                        onProjectUpdated={(p) => setProject(p)}
                        projectId={id || ""}
                        orgId={orgData?.id || ""}
                        project={project}
                        partners={project.infos.partners}
                      />
                    )}
                    {planningEditOpen && activePlanning && (
                      <PlanningSetupModal
                        onClose={() => setPlanningEditOpen(false)}
                        onUpdated={(p) => updatePlanning(p)}
                        projectId={id || ""}
                        orgId={orgData?.id || ""}
                        project={project}
                        partners={project.infos.partners}
                        existingPlanning={activePlanning}
                      />
                    )}
                    {activePlanning ? (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-h4 text-neutral-400 italic">
                            Ce planning est un planning prévisionnel
                            d'interventions et est susceptible d'évoluer.
                          </span>
                          {!isGuest && (
                            <div className="flex flex-row gap-2">
                              <SecondaryButton
                                variant="small"
                                danger
                                onClick={async () => {
                                  if (!activePlanning) return;
                                  await planningsApi.delete(activePlanning.id);
                                  removePlannings([activePlanning.id]);
                                  showNotification({
                                    type: "success",
                                    message: "Planning supprimé",
                                  });
                                }}
                              >
                                Supprimer
                              </SecondaryButton>
                              <Button
                                label="Modifier le planning"
                                onClick={() => setPlanningEditOpen(true)}
                              />
                            </div>
                          )}
                        </div>
                        <PlanningTable
                          planning={activePlanning}
                          partners={project.infos.partners}
                          onUpdate={updatePlanning}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-start gap-4 py-8">
                        <div className="text-h2 text-neutral-600 font-medium">
                          Aucun planning prévisionnel
                        </div>
                        <div className="text-h3 text-neutral-400">
                          {isGuest
                            ? "Le planning n'a pas encore été créé."
                            : "Créez un planning pour suivre les interventions semaine par semaine."}
                        </div>
                        {!isGuest && (
                          <Button
                            label="Créer le planning"
                            onClick={() => setPlanningSetupOpen(true)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {activeTab !== "planning" && (
              <InfosSection project={project} setProject={handleSetProject} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const projectTabs = [
  {
    value: "timeline",
    label: "Fil du projet",
  },
  {
    value: "steps",
    label: "Étapes",
  },
  {
    value: "photos",
    label: "Photos",
  },
  {
    value: "documents",
    label: "Documents",
  },
  {
    value: "partners",
    label: "Artisans",
  },
  {
    value: "planning",
    label: "Planning",
  },
];

const photoTabs = [
  {
    value: "date",
    label: "Date",
  },
  {
    value: "step",
    label: "Étapes",
  },
  {
    value: "gallery",
    label: "Galerie",
  },
];

const documentTabs = [
  {
    value: "folder",
    label: "Dossier",
  },
  {
    value: "date",
    label: "Date",
  },
];
